import React, { forwardRef, useState } from 'react'
import { SliderProps, useTheme } from '@mui/material'
import {
  StyledSlider,
  SliderContainer,
  SliderValueContainer,
  SliderValueBubble,
  SliderValueIndicator,
  SliderRangeValues,
  SliderRangeText,
} from './Slider.styled'

type ReusableSliderProps = SliderProps & {
  value: number
  min?: number
  max?: number
  step?: number
  minAllowedValue?: number
  customColor?: string
  hideRangeValues?: boolean
  onChange?: (event: Event, newValue: number | number[]) => void
}

export const Slider = forwardRef<HTMLSpanElement, ReusableSliderProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 10,
      step = 0.5,
      minAllowedValue = min,
      customColor,
      hideRangeValues,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme()
    const sliderColor = customColor || theme.palette.primary.main
    const [dragging, setDragging] = useState(false)

    const handleDragStart = (
      e: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => {
      setDragging(true)
      e.preventDefault()
    }

    const handleDrag = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (dragging) {
        const sliderRect =
          e.currentTarget.parentElement?.getBoundingClientRect()
        if (!sliderRect) return

        const newPosition = Math.max(
          min,
          Math.min(
            max,
            min +
              ((e.clientX - sliderRect.left) / sliderRect.width) * (max - min)
          )
        )

        const snappedValue = Math.max(
          minAllowedValue,
          Math.round(newPosition / step) * step
        )

        onChange?.(e as unknown as Event, snappedValue)
      }
    }

    const handleDragEnd = () => {
      setDragging(false)
    }

    return (
      <SliderContainer>
        <StyledSlider
          ref={ref}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          step={step}
          sliderColor={sliderColor}
          sx={{
            cursor: onChange ? 'pointer' : 'default',
          }}
          {...rest}
        />

        <SliderValueContainer
          sx={{
            left: `${((value - min) / (max - min)) * 100}%`,
            cursor: onChange ? 'pointer' : 'default',
          }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDrag}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <SliderValueBubble sliderColor={sliderColor}>
            {value?.toFixed(1)}
          </SliderValueBubble>
          <SliderValueIndicator sliderColor={sliderColor} />
        </SliderValueContainer>

        {!hideRangeValues && (
          <SliderRangeValues>
            <SliderRangeText>{min}</SliderRangeText>
            <SliderRangeText>{max}</SliderRangeText>
          </SliderRangeValues>
        )}
      </SliderContainer>
    )
  }
)
